import { Platform } from './integration.types';
import { environment } from '../config/environment';
import { useApiClient } from './api';

/**
 * Hook for user integrations service
 */
export const useUserIntegrationsService = () => {
  const apiClient = useApiClient();

  /**
   * Get user integrations from the backend
   * @returns Array of platforms
   */
  const getUserIntegrations = async (): Promise<Platform[]> => {
    try {
      const { data } = await apiClient.get(`${environment.expressApiUrl}/api/user-integrations`);
      
      console.log('Raw user integrations data:', data);
      
      // Convert API response to Platform format
      return data.map((integration: any) => ({
        name: integration.id,
        display_name: integration.displayName,
        logo_uri: integration.logoUri,
        platform_type: integration.platformType || 'exchange',
        position: 0,
        legacy: false,
        beta: false,
        custom_api_uri: '',
        custom_csv_uri: '',
        isCustom: integration.isCustom || false,
        status: integration.status || 'selected'
      }));
    } catch (error) {
      console.error('Error getting user integrations:', error);
      throw error;
    }
  };

  /**
   * Save user integrations to the backend
   * @param platforms - Array of platforms to save as integrations
   */
  const saveUserIntegrations = async (platforms: Platform[]): Promise<void> => {
    try {
      const integrations = platforms.map(platform => ({
        id: platform.name,
        name: platform.name,
        displayName: platform.display_name,
        logoUri: platform.logo_uri,
        platformType: platform.platform_type,
        isCustom: ('isCustom' in platform) ? platform.isCustom : false,
        status: platform.status || 'selected'
      }));
      
      await apiClient.post(`${environment.expressApiUrl}/api/user-integrations`, { integrations });
    } catch (error) {
      console.error('Error saving user integrations:', error);
      throw error;
    }
  };

  /**
   * Update integration status to 'integrated'
   * @param platformName - Name of the platform to update
   */
  const updateIntegrationStatus = async (platformName: string, platformType: string): Promise<void> => {
    try {
      await apiClient.put(`${environment.expressApiUrl}/api/user-integrations/status`, { 
        platformName,
        platformType,
        status: 'integrated'
      });
    } catch (error) {
      console.error('Error updating integration status:', error);
      throw error;
    }
  };

  /**
   * Mark integration as removed
   * @param platformName - Name of the platform to mark as removed
   */
  const removeIntegration = async (platformName: string, platformType: string): Promise<void> => {
    try {
      await apiClient.put(`${environment.expressApiUrl}/api/user-integrations/status`, { 
        platformName,
        platformType,
        status: 'removed'
      });
    } catch (error) {
      console.error('Error marking integration as removed:', error);
      throw error;
    }
  };

  /**
   * Get integration setup state
   * @returns Integration setup state ('pending' | 'completed')
   */
  const getIntegrationSetupState = async (): Promise<'pending' | 'completed'> => {
    try {
      const { data } = await apiClient.get(`${environment.expressApiUrl}/api/user-integrations/setup-state`);
      return data.integrationSetupState || 'pending';
    } catch (error) {
      console.error('Error getting integration setup state:', error);
      return 'pending';
    }
  };

  return {
    getUserIntegrations,
    saveUserIntegrations,
    updateIntegrationStatus,
    removeIntegration,
    getIntegrationSetupState
  };
};




